import {
  AspectRatio,
  Box,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  Flex,
  Image,
  Text,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { BenefitDigitalContentThumbnailContainer } from '~/components/collection/BenefitDigitalContentThumbnailContainer';
import { PreviewBenefitDigitalContentContainer } from '~/components/collection/PreviewBenefitDigitalContentContainer';
import { isGachaContentDownloadable } from '~/lib/utils/gacha';
import type { BenefitPrize } from '~/types/resources/benefitPrize';
import type { GachaDetail } from '~/types/resources/gachaDetail';
import type { GrantedItem } from '~/types/resources/grantedItem';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  benefitPrize: BenefitPrize | null;
  grantedItem: GrantedItem | null;
  gachaDownloadSetting: GachaDetail['content_download'];
};

export const CollectionBenefitModal: React.FC<Props> = (props: Props) => {
  if (props.benefitPrize == null) return null;

  const isDownloadable: boolean = useMemo(() => {
    return isGachaContentDownloadable(props.gachaDownloadSetting);
  }, [props.gachaDownloadSetting]);
  const grantedBenefits = props.grantedItem?.benefits ?? [];
  const grantedBenefit = grantedBenefits.find((grantedBenefit) => {
    return grantedBenefit.benefit.id == props.benefitPrize?.id;
  });

  const quantity: string | null = useMemo(() => {
    if (grantedBenefit == null) return null;
    if (grantedBenefit.quantity > 99) {
      return '99+';
    }
    if (grantedBenefit.quantity < 1) {
      return null;
    }
    return grantedBenefit.quantity.toString();
  }, [grantedBenefit]);

  const imageUrl = useMemo(() => {
    if (quantity == null) return props.benefitPrize?.thumbnail_image_url;
    if (grantedBenefit?.benefit?.secret_image_url != '')
      return grantedBenefit?.benefit?.secret_image_url;
    return grantedBenefit?.benefit?.image_url ?? imageUrl;
  }, [props.benefitPrize, grantedBenefit, quantity]);

  const haveDownloadContent = useMemo(() => {
    if (grantedBenefit == null) return false;
    return (
      grantedBenefit.benefit.digital_content_url != null &&
      grantedBenefit.benefit.digital_content_url != ''
    );
  }, [grantedBenefit]);

  return (
    <DialogRoot
      open={props.isOpen}
      scrollBehavior="inside"
      onInteractOutside={props.onClose}
      initialFocusEl={() => null}
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent mx="32px" my={0} alignSelf="center">
          <DialogHeader pt="8px" pb="0" px="8px">
            <Flex flexDir="row" w="full" justify="right" align="end">
              <Image
                src="/assets/icon/close.svg"
                alt="icon"
                h="28px"
                w="28px"
                onClick={props.onClose}
              />
            </Flex>
          </DialogHeader>
          <DialogBody>
            <Flex justify="center" w="full">
              {!haveDownloadContent ? (
                <AspectRatio ratio={1} w="full" maxH="400px">
                  <Image
                    src={imageUrl}
                    alt={`${props.benefitPrize.name}の画像`}
                    loading="lazy"
                    pointerEvents="none"
                    w="full"
                    maxH="400px"
                  />
                </AspectRatio>
              ) : (
                <BenefitDigitalContentThumbnailContainer
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  grantedBenefit={grantedBenefit!}
                />
              )}
            </Flex>
            <Box h="16px" />
            <Text
              fontSize="13px"
              fontWeight="bold"
              mx="40px"
              px="8px"
              textAlign="center"
            >
              {props.benefitPrize.name}
            </Text>
            {grantedBenefit && quantity != null && (
              <>
                <Box h="16px" />
                <Text
                  fontSize="11px"
                  fontWeight="bold"
                  mx="40px"
                  px="8px"
                  textAlign="center"
                >
                  {quantity}個取得済み
                </Text>
                {haveDownloadContent && (
                  <>
                    <Box h="16px" />
                    <Flex justify="center" alignItems="center" flexDir="column">
                      <PreviewBenefitDigitalContentContainer
                        grantedBenefit={grantedBenefit}
                        isDownloadable={isDownloadable}
                        gachaDownloadSetting={props.gachaDownloadSetting}
                      />
                    </Flex>
                  </>
                )}
              </>
            )}
            <Box h="32px" />
          </DialogBody>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};
